import React, { useState, useEffect } from 'react';
import {
  Table, Card, Button, Modal, Form, Input, Select,
  Space, Tag, Typography, Statistic, Row, Col, message,
  Popconfirm, Slider, Tabs, Alert, List, Collapse, Tooltip
} from 'antd';
import {
  SearchOutlined, MergeCellsOutlined, LinkOutlined,
  DeleteOutlined, CheckOutlined, CloseOutlined,
  ExclamationCircleOutlined, SyncOutlined, PieChartOutlined
} from '@ant-design/icons';
import { importService, clientVendorService } from '../services/api';
import ReactECharts from 'echarts-for-react';

const { Title, Text } = Typography;
const { Option } = Select;

const DataCleansing = () => {
  const [loading, setLoading] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [threshold, setThreshold] = useState(85);
  const [isAliasModalVisible, setIsAliasModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('duplicates');
  const [form] = Form.useForm();

  const fetchDuplicateGroups = async () => {
    try {
      const data = await importService.getDuplicateGroups();
      setDuplicateGroups(data);
    } catch (error) {
      console.error('Failed to load duplicate groups:', error);
    }
  };

  const fetchAliases = async () => {
    try {
      const data = await importService.getAliases();
      setAliases(data);
    } catch (error) {
      console.error('Failed to load aliases:', error);
    }
  };

  const fetchCategoryData = async () => {
    try {
      const data = await importService.getCategoryDistribution();
      setCategoryData(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const data = await clientVendorService.getAll();
      setVendors(data);
    } catch (error) {
      console.error('Failed to load vendors:', error);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchDuplicateGroups();
    fetchAliases();
    fetchCategoryData();
  }, []);

  const handleDetectDuplicates = async (saveResults = false) => {
    try {
      setLoading(true);
      const results = await importService.detectDuplicates(threshold, saveResults);
      setDuplicateResults(results);

      if (saveResults) {
        message.success(`Found ${results.total_duplicates_found} potential duplicates`);
        fetchDuplicateGroups();
      }
    } catch (error) {
      message.error('Failed to detect duplicates');
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (groupId, canonicalVendorId) => {
    try {
      await importService.mergeDuplicateGroup(groupId, canonicalVendorId);
      message.success('Suppliers merged successfully');
      fetchDuplicateGroups();
      fetchAliases();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Merge failed');
    }
  };

  const handleDismissGroup = async (groupId) => {
    try {
      await importService.updateDuplicateGroup(groupId, { status: 'dismissed' });
      message.success('Group dismissed');
      fetchDuplicateGroups();
    } catch (error) {
      message.error('Failed to dismiss group');
    }
  };

  const handleCreateAlias = async (values) => {
    try {
      await importService.createAlias({
        vendor_id: values.vendor_id,
        alias_name: values.alias_name,
        source: 'manual',
      });
      message.success('Alias created');
      setIsAliasModalVisible(false);
      form.resetFields();
      fetchAliases();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to create alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    try {
      await importService.deleteAlias(aliasId);
      message.success('Alias deleted');
      fetchAliases();
    } catch (error) {
      message.error('Failed to delete alias');
    }
  };

  const getStatusTag = (status) => {
    const config = {
      pending: { color: 'orange', text: 'Pending Review' },
      reviewed: { color: 'blue', text: 'Reviewed' },
      merged: { color: 'green', text: 'Merged' },
      dismissed: { color: 'default', text: 'Dismissed' },
    };
    const { color, text } = config[status] || { color: 'default', text: status };
    return <Tag color={color}>{text}</Tag>;
  };

  const categoryChartOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      right: 10,
      top: 'center',
    },
    series: [
      {
        name: 'Category',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold'
          }
        },
        data: categoryData.map(cat => ({
          value: cat.count,
          name: cat.category || 'Uncategorized',
        })),
      }
    ]
  };

  const aliasColumns = [
    {
      title: 'Alias Name',
      dataIndex: 'alias_name',
      key: 'alias_name',
    },
    {
      title: 'Canonical Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      render: (name) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source) => (
        <Tag color={source === 'manual' ? 'purple' : 'cyan'}>
          {source?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Delete this alias?"
          onConfirm={() => handleDeleteAlias(record.id)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'duplicates',
      label: (
        <span>
          <MergeCellsOutlined />
          Duplicate Detection
        </span>
      ),
      children: (
        <>
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={24} align="middle">
              <Col flex="1">
                <Text strong>Similarity Threshold: {threshold}%</Text>
                <Slider
                  min={50}
                  max={100}
                  value={threshold}
                  onChange={setThreshold}
                  marks={{ 50: '50%', 70: '70%', 85: '85%', 100: '100%' }}
                />
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<SearchOutlined />}
                    onClick={() => handleDetectDuplicates(false)}
                    loading={loading}
                  >
                    Preview Duplicates
                  </Button>
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={() => handleDetectDuplicates(true)}
                    loading={loading}
                  >
                    Detect & Save
                  </Button>
                </Space>
              </Col>
            </Row>

            {duplicateResults && (
              <Alert
                message={`Found ${duplicateResults.total_duplicates_found} potential duplicates among ${duplicateResults.vendors_analyzed} vendors`}
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>

          {/* Preview Results */}
          {duplicateResults && duplicateResults.groups && duplicateResults.groups.length > 0 && (
            <Card title="Detection Results" style={{ marginBottom: 24 }}>
              <Collapse>
                {duplicateResults.groups.map((group, index) => (
                  <Collapse.Panel
                    key={index}
                    header={
                      <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        <Text strong>{group.canonical_name || group.members?.[0]?.vendor_name}</Text>
                        <Tag>{group.members?.length || 0} suppliers</Tag>
                        <Tag color="blue">{Math.round(group.avg_similarity || 0)}% similar</Tag>
                      </Space>
                    }
                  >
                    <List
                      size="small"
                      dataSource={group.members || []}
                      renderItem={(member) => (
                        <List.Item>
                          <Space>
                            <span>{member.vendor_name}</span>
                            <Tag>{member.similarity_score}%</Tag>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </Collapse.Panel>
                ))}
              </Collapse>
            </Card>
          )}

          {/* Saved Groups */}
          <Card title="Saved Duplicate Groups for Review">
            {duplicateGroups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
                No duplicate groups saved. Run detection and save results to review.
              </div>
            ) : (
              <Collapse>
                {duplicateGroups.filter(g => g.status === 'pending').map((group) => (
                  <Collapse.Panel
                    key={group.id}
                    header={
                      <Space>
                        <Text strong>{group.canonical_name || 'Unnamed Group'}</Text>
                        <Tag>{group.members?.length || 0} suppliers</Tag>
                        {getStatusTag(group.status)}
                      </Space>
                    }
                    extra={
                      <Space onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Dismiss group">
                          <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => handleDismissGroup(group.id)}
                          />
                        </Tooltip>
                      </Space>
                    }
                  >
                    <List
                      size="small"
                      dataSource={group.members || []}
                      renderItem={(member) => (
                        <List.Item
                          actions={[
                            <Button
                              type="primary"
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={() => handleMerge(group.id, member.vendor_id)}
                            >
                              Set as Canonical
                            </Button>
                          ]}
                        >
                          <Space>
                            <span>{member.vendor?.vendor_name || `Vendor #${member.vendor_id}`}</span>
                            <Tag color="blue">{member.similarity_score}% match</Tag>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </Collapse.Panel>
                ))}
              </Collapse>
            )}
          </Card>
        </>
      ),
    },
    {
      key: 'aliases',
      label: (
        <span>
          <LinkOutlined />
          Supplier Aliases
        </span>
      ),
      children: (
        <Card
          title="Supplier Aliases"
          extra={
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={() => setIsAliasModalVisible(true)}
            >
              Add Alias
            </Button>
          }
        >
          <Table
            columns={aliasColumns}
            dataSource={aliases}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: 'No aliases created yet. Aliases help match different vendor name variations.',
            }}
          />
        </Card>
      ),
    },
    {
      key: 'categories',
      label: (
        <span>
          <PieChartOutlined />
          Category Distribution
        </span>
      ),
      children: (
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card title="Vendors by Category">
              {categoryData.length > 0 ? (
                <ReactECharts
                  option={categoryChartOption}
                  style={{ height: 400 }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
                  No category data available
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Category Breakdown">
              <Table
                dataSource={categoryData}
                rowKey="category"
                pagination={false}
                columns={[
                  {
                    title: 'Category',
                    dataIndex: 'category',
                    key: 'category',
                    render: (cat) => cat || 'Uncategorized',
                  },
                  {
                    title: 'Vendors',
                    dataIndex: 'count',
                    key: 'count',
                    sorter: (a, b) => a.count - b.count,
                  },
                  {
                    title: 'Total Spend',
                    dataIndex: 'total_spend',
                    key: 'total_spend',
                    render: (val) => `$${(val || 0).toLocaleString()}`,
                    sorter: (a, b) => (a.total_spend || 0) - (b.total_spend || 0),
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>Data Cleansing</Title>
      <Text type="secondary">
        Detect duplicate suppliers, manage aliases, and standardize categories
      </Text>

      {/* Stats */}
      <Row gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Vendors"
              value={vendors.length}
              prefix={<SearchOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending Groups"
              value={duplicateGroups.filter(g => g.status === 'pending').length}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Aliases"
              value={aliases.length}
              prefix={<LinkOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Categories"
              value={categoryData.length}
              prefix={<PieChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />

      {/* Add Alias Modal */}
      <Modal
        title="Add Supplier Alias"
        open={isAliasModalVisible}
        onCancel={() => {
          setIsAliasModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateAlias}>
          <Form.Item
            name="vendor_id"
            label="Canonical Vendor"
            rules={[{ required: true, message: 'Please select a vendor' }]}
          >
            <Select
              showSearch
              placeholder="Select the primary vendor"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {vendors.map((cv) => (
                <Option key={cv.id} value={cv.id}>
                  {cv.vendor?.name || cv.vendor_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="alias_name"
            label="Alias Name"
            rules={[{ required: true, message: 'Please enter the alias' }]}
            extra="Enter a name variation that should map to this vendor"
          >
            <Input placeholder="e.g., MSFT, Microsoft Inc, Microsoft Corporation" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsAliasModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Create Alias</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DataCleansing;
